import React, { useState } from 'react';
import {
  User,
  Mail,
  Phone,
  MessageCircle,
  Languages,
  Euro,
  Check,
  Loader,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSupabase } from '../context/SupabaseContext';
import { baseStudent,baseTeacher, baseParent, Parent, routes_per_type, Student, Teacher } from '../components/interfaces/All';

interface NewStudentInterface {
  account_type: 'student'| 'parent' | 'teacher';
}

const NewStudent: React.FC<NewStudentInterface> = ({account_type='student'}) => {
  const navigate = useNavigate();
  const makeInitialFormData = (account_type: 'student'| 'parent' | 'teacher'): Student | Teacher | Parent => {
    switch (account_type) {
      case 'student':
        return baseStudent;
      case 'teacher':
        return baseTeacher;
      case 'parent':
        return baseParent;
    }
  };

  const [formData, setFormData] = useState<Student | Teacher | Parent>(
    () => makeInitialFormData(account_type)
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const {state, createStudent, createTeacher, createParent } = useSupabase();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof Student]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';

    setErrors(newErrors);
    // console.log(newErrors)
    return Object.keys(newErrors).length === 0;
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {

      console.log(account_type)
      switch(account_type){
        case 'student':
          formData.teacher_id = [state.userProfile.id]
          createStudent(formData as Student);
        break;
        case 'teacher':
          createTeacher(formData as Teacher);
        break;
        case 'parent':
          formData.teacher_id = [state.userProfile.id]
          createParent(formData as Parent);
        break
      }

      console.log(`Creating ${account_type}:`, formData);
      setSubmitSuccess(true);

      // Redirect after 1.5s
      setTimeout(() => {
        navigate(routes_per_type[account_type]);
      }, 1500);

    } catch (error) {
      console.error(`Failed to create ${account_type}:`, error);
      setErrors({ submit: `Failed to create ${account_type}. Please try again.` });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = formData.last_name && formData.first_name && formData.email && !isSubmitting;

  return (
    <div className="fade-in max-w-md mx-auto">
      {/* Header */}
      <div className="mb-8">

        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-primary/10 rounded-3xl flex items-center justify-center mx-auto mb-4">
            <User className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">New { account_type == 'student'? 'Student':
                account_type == 'teacher'? 'Teacher':
                account_type == 'parent'? 'Parent': 'Account'
              }</h1>
          {/* <p className="text-text-light">{`Fill in the details to add a new ${account_type}`}</p> */}
        </div>
      </div>

      {submitSuccess ? (
        <div className="card p-12 text-center">
          <Check className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-emerald-600 mb-2">{
              account_type == 'student'? 'Student' :
              account_type == 'teacher'? 'Teacher' :
              account_type == 'parent'? 'Parent' : 'Account'
            } Created!</h3>
          <p className="text-text-light">{`Redirecting to ${account_type}s list...`}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div className="card p-4">
            <label className="block text-sm font-semibold text-text-light mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              First Name *
            </label>
            <input
              name="first_name"
              type="text"
              placeholder="e.g. Emma"
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 dark:bg-gray-800 focus:ring-primary focus:border-transparent transition-all ${
                errors.name ? 'border-red-300 bg-red-50 dark:bg-red-900/10' : 'border-border'
              }`}
              value={formData.first_name}
              onChange={handleChange}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.name}
              </p>
            )}
          </div>
          <div className="card p-4">
            <label className="block text-sm font-semibold text-text-light mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Last Name *
            </label>
            <input
              name="last_name"
              type="text"
              placeholder="e.g. Dupont"
              className={`w-full px-4 py-3 border rounded-xl dark:bg-gray-800 focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                errors.name ? 'border-red-300 bg-red-50 dark:bg-red-900/10' : 'border-border'
              }`}
              value={formData.last_name}
              onChange={handleChange}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.name}
              </p>
            )}
          </div>

          {/* Email */}
          <div className="card p-4">
            <label className="block text-sm font-semibold text-text-light mb-3 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email *
            </label>
            <input
              name="email"
              type="email"
              placeholder="emma.dupont@email.com"
              className={`w-full px-4 py-3 border rounded-xl dark:bg-gray-800 focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                errors.email ? 'border-red-300 bg-red-50 dark:bg-red-900/10' : 'border-border'
              }`}
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.email}
              </p>
            )}
          </div>

          {/* Phone (optional) */}
          <div className="card p-4">
            <label className="block text-sm font-semibold text-text-light mb-3 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Phone (optional)
            </label>
            <input
              name="phone"
              type="tel"
              placeholder="+33 6 12 34 56 78"
              className="w-full px-4 py-3 border border-border rounded-xl dark:bg-gray-800 focus:ring-2 focus:ring-primary focus:border-transparent"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>

          {/* Notes */}
          <div className="card p-4">
            <label className="block text-sm font-semibold text-text-light mb-3 flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              Notes (optional)
            </label>
            <textarea
              name="notes"
              rows={3}
              placeholder={`Any important information about this ${account_type}...`}
              className="w-full px-4 py-3 border border-border dark:bg-gray-800 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              value={formData.notes}
              onChange={handleChange}
            />
          </div>

          {/* Starting Balance */}
          {/* {account_type === 'student' && (
            <div className="card p-4">
              <label className="block text-sm font-semibold text-text-light mb-3 flex items-center gap-2">
                <Euro className="w-4 h-4" />
                Starting Balance (€)
              </label>
              <input
                name="startingBalance"
                type="number"
                min="0"
                step="0.01"
                placeholder="0"
                className="w-full px-4 py-3 border border-border rounded-xl dark:bg-gray-800 focus:ring-2 focus:ring-primary focus:border-transparent"
                value={formData.balance}
                onChange={handleChange}
              />
            </div>
          )} */}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!canSubmit}
            className={`w-full btn ${canSubmit ? 'btn-primary' : 'btn-disabled'} py-4 text-lg flex items-center justify-center gap-3`}
          >
            {isSubmitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Creating Student...
              </>
            ) : (
              <>
                {canSubmit ? (<>
                  <Check className="w-5 h-5" />
                  Create Student
                </>):(<>
                  <AlertCircle size={40}className="text-red-500" />
                  Please fill in all required fields to create a new {account_type}.
                </>)}
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default NewStudent;
